/**
 * When to stop asking for more rows.
 *
 * Two signals, and both are needed. `X-Total-Count` is the one that lets a picker say "25 of
 * 240", but it can be absent — the API is on another origin, so the header only reaches this
 * code if core-api exposes it through CORS. A short page is therefore the authority on the end
 * of the list, and the total is the authority on how far off the end is. Trusting the total
 * alone would page forever against a server that stopped early; trusting neither would page
 * into an empty response every time, which is a wasted round trip on every list that happens
 * to divide evenly by the page size.
 */

export interface PageLike {
  items: readonly unknown[];
  total: number | null;
}

/** The offset to request next, or `undefined` when this is the end. */
export function nextPageOffset(pages: readonly PageLike[], pageSize: number): number | undefined {
  const last = pages.at(-1);
  if (!last || last.items.length < pageSize) return undefined;
  const loaded = pages.reduce((n, p) => n + p.items.length, 0);
  if (last.total !== null && loaded >= last.total) return undefined;
  return loaded;
}

/** `X-Total-Count` as a number, or null when the server did not say — which is not zero, and
 *  must not be rendered as though it were. */
export function totalFromHeader(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}
